import React, { useEffect, useState } from "react";
import Icon from "./Icon";
import { apiGet, apiSend, API } from "../api";
import { C, R, S } from "../theme";

const isImage = (mime = "", name = "") => /^image\//i.test(mime) || /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(name);

export const docTypeIcon = (mime, name) => (isImage(mime, name) ? "image" : "document-text");

const isIOS = typeof navigator !== "undefined" && (/iP(hone|od|ad)/.test(navigator.platform || "") || /iPad|iPhone|iPod/i.test(navigator.userAgent || "") || (/Mac/.test(navigator.userAgent || "") && "ontouchend" in document));

const btnBase = { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", minHeight: 52, borderRadius: R.md, fontWeight: 800, fontSize: 15, border: "none", marginBottom: S.sm, cursor: "pointer" };
const primaryBtn = { ...btnBase, background: C.amber, color: C.black };
const deleteBtn = { ...btnBase, background: C.card2, color: C.red, border: `1px solid ${C.borderStrong}` };
const ghostBtn = { ...btnBase, background: "transparent", color: C.muted, minHeight: 44 };
const dangerBtn = { ...btnBase, background: C.red, color: "#fff" };
const panelStyle = { background: C.card, width: "100%", maxWidth: 420, borderRadius: R.lg, padding: S.xl, border: `1px solid ${C.borderStrong}`, boxShadow: "0 24px 70px rgba(0,0,0,0.65)" };
const roundBtn = { width: 40, height: 40, borderRadius: 20, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", fontSize: 22, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 };

// Uploaded-document controls with a fully IN-APP preview lightbox (no new tabs / no window.open,
// so iOS Safari never popup-blocks). Signed inline URL is pre-fetched when the sheet opens.
export default function DocViewer({ doc, canDelete = false, onClose, onDeleted, onUpdated }) {
  const [mode, setMode] = useState("menu"); // menu | preview | confirm
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [zoom, setZoom] = useState(1);
  const [pdfMode, setPdfMode] = useState("native"); // native | gview
  const [expiry, setExpiry] = useState("");

  useEffect(() => {
    setMode("menu"); setErr(""); setBusy(false); setFileUrl(""); setZoom(1);
    setPdfMode(isIOS ? "gview" : "native");
    setExpiry(doc?.expiry_date || "");
    if (!doc) return;
    let cancelled = false;
    (async () => {
      try {
        const { exp, sig } = await apiGet(`/documents/${doc.id}/signed`);
        if (!cancelled) setFileUrl(`${API}/documents/${doc.id}/view?exp=${exp}&sig=${sig}`);
      } catch (e) { if (!cancelled) setErr("Could not prepare this document. Please try again."); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);

  if (!doc) return null;

  const close = () => onClose && onClose();
  const image = isImage(doc.mime, doc.name);

  const doDelete = async () => {
    setBusy(true); setErr("");
    try { await apiSend(`/documents/${doc.id}`, "DELETE"); onDeleted && onDeleted(doc.id); close(); }
    catch (e) { setErr("Delete failed. Please try again."); }
    finally { setBusy(false); }
  };

  const saveExpiry = async () => {
    setBusy(true); setErr("");
    try { await apiSend(`/documents/${doc.id}`, "PATCH", { expiry_date: expiry || "" }); onUpdated && onUpdated(); close(); }
    catch (e) { setErr("Could not save the expiry date."); }
    finally { setBusy(false); }
  };

  // ---- In-app preview lightbox ----
  if (mode === "preview" && fileUrl) {
    const gviewSrc = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    return (
      <div data-testid="doc-preview-modal" style={{ position: "fixed", inset: 0, zIndex: 12000, background: "#0b0f19", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: `calc(env(safe-area-inset-top,12px) + 10px) ${S.md}px ${S.sm}px`, borderBottom: "1px solid rgba(255,255,255,0.12)", flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.name}</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{doc.category || (image ? "Image" : "PDF")}</div>
          </div>
          {image && (
            <>
              <button data-testid="doc-image-zoom-out" className="pressable" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} style={roundBtn}>−</button>
              <button data-testid="doc-image-zoom-in" className="pressable" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} style={roundBtn}>+</button>
            </>
          )}
          <button data-testid="doc-preview-close" className="pressable" onClick={close} style={{ ...roundBtn, background: C.amber, borderColor: C.amber }}><Icon name="close" size={22} color={C.black} /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: "auto", WebkitOverflowScrolling: "touch", touchAction: "pinch-zoom", display: "flex", alignItems: image ? "center" : "stretch", justifyContent: "center", background: image ? "#0b0f19" : "#525659" }}>
          {image ? (
            <img data-testid="doc-preview-image" src={fileUrl} alt={doc.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 0.15s ease", borderRadius: 6 }} />
          ) : pdfMode === "gview" ? (
            <iframe data-testid="doc-pdf-gview" title={doc.name} src={gviewSrc} style={{ width: "100%", height: "100%", minHeight: "80vh", border: "none", background: "#fff" }} />
          ) : (
            <object data-testid="doc-pdf-object" data={fileUrl} type="application/pdf" style={{ width: "100%", height: "100%", minHeight: "80vh" }}>
              <iframe title={doc.name} src={fileUrl} style={{ width: "100%", height: "100%", minHeight: "80vh", border: "none" }} />
            </object>
          )}
        </div>

        <div style={{ padding: `${S.sm}px ${S.md}px calc(env(safe-area-inset-bottom,12px) + 10px)`, borderTop: "1px solid rgba(255,255,255,0.12)", flexShrink: 0 }}>
          {!image && (
            <button data-testid="doc-pdf-fallback-toggle" className="pressable" onClick={() => setPdfMode((m) => (m === "gview" ? "native" : "gview"))} style={{ ...ghostBtn, color: "rgba(255,255,255,0.85)", minHeight: 40, marginBottom: S.xs }}>
              {pdfMode === "gview" ? "Not loading? Try the native viewer" : "Not loading? Try the compatibility viewer"}
            </button>
          )}
          <button data-testid="doc-preview-close-btn" className="pressable" onClick={close} style={primaryBtn}>Close Preview</button>
        </div>
      </div>
    );
  }

  // ---- Action chooser / delete confirm ----
  return (
    <div className="modal-backdrop modal-center" onClick={close}>
      <div data-testid={mode === "confirm" ? "doc-delete-confirm" : "doc-actions-menu"} onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: S.md, marginBottom: S.lg }}>
          <div style={{ width: 46, height: 46, borderRadius: 12, background: C.card2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={docTypeIcon(doc.mime, doc.name)} size={24} color={C.blue} /></div>
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ color: C.text, fontWeight: 800, fontSize: 16, wordBreak: "break-word" }}>{doc.name}</div>{doc.category && <div style={{ color: C.muted, fontSize: 12 }}>{doc.category}</div>}</div>
        </div>
        {!!err && <div data-testid="doc-action-error" style={{ color: C.red, fontWeight: 700, marginBottom: S.md }}>{err}</div>}
        {mode === "confirm" ? (
          <>
            <div style={{ color: C.text, fontSize: 15, marginBottom: S.lg }}>Are you sure you want to delete this document? This cannot be undone.</div>
            <button data-testid="doc-delete-confirm-btn" disabled={busy} className="pressable" onClick={doDelete} style={dangerBtn}><Icon name="trash" size={20} color="#fff" /> {busy ? "Deleting…" : "Delete Document"}</button>
            <button data-testid="doc-delete-cancel" className="pressable" onClick={() => { setErr(""); setMode("menu"); }} style={ghostBtn}>Cancel</button>
          </>
        ) : (
          <>
            <button data-testid="doc-action-view" disabled={!fileUrl} className="pressable" onClick={() => setMode("preview")} style={{ ...primaryBtn, opacity: fileUrl ? 1 : 0.6 }}><Icon name="eye" size={20} color={C.black} /> {fileUrl ? "View Document" : "Preparing…"}</button>
            {canDelete && (
              <div style={{ margin: `${S.sm}px 0`, padding: S.md, background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}` }}>
                <div style={{ color: C.sub, fontSize: 12, fontWeight: 800, marginBottom: 8, textTransform: "uppercase" }}>Expiry / Renewal Date</div>
                <div style={{ display: "flex", gap: S.sm }}>
                  <input data-testid="doc-edit-expiry-input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={{ flex: 1, background: C.card, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 48, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
                  <button data-testid="doc-edit-expiry-save" disabled={busy} className="pressable" onClick={saveExpiry} style={{ background: C.amber, color: C.black, border: "none", borderRadius: R.md, minHeight: 48, padding: `0 ${S.lg}px`, fontWeight: 900, fontSize: 15 }}>{busy ? "…" : "Save"}</button>
                </div>
              </div>
            )}
            {canDelete && <button data-testid="doc-action-delete" className="pressable" onClick={() => { setErr(""); setMode("confirm"); }} style={deleteBtn}><Icon name="trash" size={20} color={C.red} /> Delete</button>}
            <button data-testid="doc-action-cancel" className="pressable" onClick={close} style={ghostBtn}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}
